import type { Sale } from '@/types';
import api from '@/services/api';

// ============================================================================
// Integración con controlador fiscal HASAR (modelo SMH/PT-250F).
//
// El navegador NO le habla directo al controlador (USB/serie). En la PC de la caja
// corre un "agente local" (bridge) que expone un endpoint HTTP y traduce este JSON a
// los comandos del protocolo Hasar. Acá armamos el documento y se lo mandamos al agente.
//
// El contrato (JSON de abajo) es nuestro; el agente lo mapea a Hasar. La URL del agente
// se configura por caja (localStorage), default http://localhost:12000.
// ============================================================================

const AGENT_KEY = 'fiscalAgentUrl';
const DEFAULT_AGENT = 'http://localhost:12000';

export function getFiscalAgentUrl(): string {
  try {
    return localStorage.getItem(AGENT_KEY) || DEFAULT_AGENT;
  } catch {
    return DEFAULT_AGENT;
  }
}

export function setFiscalAgentUrl(url: string): void {
  try {
    localStorage.setItem(AGENT_KEY, url.trim());
  } catch {
    /* ignore */
  }
}

export interface FiscalOptions {
  cuit?: string;
}

/** Identificador que devuelve el agente cuando la jornada quedó sin cerrar. */
export const CIERRE_Z_PENDIENTE = 'CIERRE_Z_PENDIENTE';

export class FiscalAgentError extends Error {
  /** Código del agente/controlador: CIERRE_Z_PENDIENTE, COMPROBANTE_ABIERTO, ErrorParametro… */
  identificador?: string;
  /** Comando que el controlador rechazó (AbrirDocumento, ImprimirItem…) */
  comando?: string;
  /** Secuencia de comandos/respuestas, si el agente la informa */
  trace?: string;
  /** true si lo único que falta es que el cajero haga el Cierre Z */
  get needsCierreZ(): boolean {
    return this.identificador === CIERRE_Z_PENDIENTE;
  }
  constructor(message: string, identificador?: string) {
    super(message);
    this.name = 'FiscalAgentError';
    this.identificador = identificador;
  }
}

/**
 * El agente contesta los errores como JSON ({ok:false, error, identificador}).
 * Sin esto el cajero terminaba viendo el JSON crudo en el toast.
 */
// Timeout para las llamadas al agente local. `fetch` sin AbortController espera para
// SIEMPRE: si el agente esta levantado pero la impresora quedo trabada (sin papel, con
// un comprobante abierto), la pantalla se cuelga y no hay forma de salir.
// El tope es MUY generoso a proposito: en produccion se midieron impresiones
// legitimas de 130 segundos (ver bitacora del 10/09/2026). Cortar antes de tiempo
// es peor que esperar: la impresora puede emitir el comprobante igual y quedaria
// uno fiscal sin registrar. Esto existe solo para que la pantalla no quede colgada
// para siempre cuando el agente ya no va a contestar nunca.
const AGENT_TIMEOUT_MS = 300000;

async function fetchAgente(url: string, init: RequestInit = {}): Promise<Response> {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), AGENT_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } catch (e: any) {
    if (e?.name === 'AbortError') {
      // Ojo: que se haya cortado la espera NO significa que no se haya emitido.
      // El cajero tiene que mirar el papel antes de reintentar.
      throw new FiscalAgentError(
        'La impresora fiscal no respondio a tiempo. Fijate en el papel si el ' +
        'comprobante salio ANTES de reintentar, para no emitirlo dos veces.'
      );
    }
    // "Failed to fetch" es lo que tira el navegador cuando no puede ni conectarse
    // al agente, y no le dice nada a nadie. Pasa siempre por lo mismo: el agente
    // no esta corriendo, o estan operando desde otra PC (escucha solo en local).
    // NO llego a la impresora, asi que seguro no se emitio nada.
    throw new FiscalAgentError(
      'No se puede contactar el agente fiscal de esta computadora. No se emitio ' +
      'nada. Fijate que el agente este andando (abri http://localhost:12000/estado ' +
      'en la PC de la caja) y que estes usando la computadora que tiene conectada ' +
      'la impresora fiscal.',
      'AGENTE_NO_DISPONIBLE'
    );
  } finally {
    clearTimeout(id);
  }
}

async function fiscalError(res: Response, fallback: string): Promise<FiscalAgentError> {
  const txt = await res.text().catch(() => '');
  try {
    const body = JSON.parse(txt);
    if (body?.error) {
      const err = new FiscalAgentError(String(body.error), body.identificador);
      err.comando = body.comando;
      err.trace = body.trace;
      return err;
    }
  } catch {
    /* no era JSON: usamos el texto tal cual */
  }
  return new FiscalAgentError(txt || `${fallback} (${res.status})`);
}

// ---------------------------------------------------------------------------
// Bitácora central
//
// El log del agente vive en la PC de la caja: para verlo hay que ir al local.
// Como el navegador ya está autenticado, le manda al backend una copia de cada
// operación — salgan bien o mal. Las exitosas importan tanto como las fallidas:
// son el punto de comparación cuando hay que entender por qué falló una.
// ---------------------------------------------------------------------------
type ReporteFiscal = {
  operacion: 'imprimir' | 'nota-credito' | 'reimprimir' | 'cierre-z' | 'reporte-x';
  ok: boolean;
  sale_id?: number | null;
  numero_comprobante?: string | null;
  identificador?: string | null;
  comando?: string | null;
  mensaje?: string | null;
  trace?: string | null;
  duracion_ms?: number;
};

async function reportar(r: ReporteFiscal): Promise<void> {
  try {
    await api.post('/fiscal/operaciones', r);
  } catch {
    /* el registro nunca puede romper la venta: si falla, se pierde y ya */
  }
}

/** Corre la operación fiscal y deja constancia del resultado, sin alterarlo. */
async function conRegistro<T>(
  operacion: ReporteFiscal['operacion'],
  saleId: number | null | undefined,
  fn: () => Promise<T>,
  numeroDe: (r: T) => string | null | undefined = (r) => (r as any) ?? null,
): Promise<T> {
  const t0 = Date.now();
  try {
    const r = await fn();
    reportar({
      operacion, ok: true, sale_id: saleId ?? null,
      numero_comprobante: numeroDe(r) ?? null,
      duracion_ms: Date.now() - t0,
    });
    return r;
  } catch (e: any) {
    reportar({
      operacion, ok: false, sale_id: saleId ?? null,
      identificador: e?.identificador ?? null,
      comando: e?.comando ?? null,
      mensaje: e?.message ?? String(e),
      trace: e?.trace ?? null,
      duracion_ms: Date.now() - t0,
    });
    throw e;
  }
}

/**
 * Arma el comprobante fiscal (Monotributo -> Ticket/Factura C) y lo envía al agente
 * local del controlador Hasar. Lanza error si el agente no responde o rechaza.
 */
export async function printFiscal(sale: Sale, opts?: FiscalOptions): Promise<string | null> {
  return conRegistro('imprimir', sale.id, () => _printFiscal(sale, opts));
}

async function _printFiscal(sale: Sale, opts?: FiscalOptions): Promise<string | null> {
  const base = getFiscalAgentUrl().replace(/\/$/, '');

  const payload = {
    // Monotributo: comprobante tipo C, sin IVA discriminado
    tipo_comprobante: 'C',
    condicion_iva_emisor: 'monotributo',
    emisor_cuit: opts?.cuit || '',
    cliente: {
      nombre: sale.customer_name || 'Consumidor Final',
      documento: sale.customer_document || '',
    },
    items: (sale.items || []).map((it) => ({
      descripcion: it.product_name || it.custom_name || 'Producto',
      cantidad: Number(it.quantity) || 1,
      precio_unitario: Number(it.unit_price) || 0,
      importe: Number(it.subtotal) || 0,
      alicuota_iva: 0, // monotributo
    })),
    // Descuento efectivo = subtotal - total. Cubre % , monto fijo, canje VIP y pago dividido
    // (discount_amount queda en 0 para los porcentuales, por eso no se puede usar directo).
    descuento: Math.max(0, (Number(sale.subtotal) || 0) - (Number(sale.total) || 0)),
    total: Number(sale.total) || 0,
    forma_pago: sale.payment_method,
    referencia_interna: sale.receipt_number || String(sale.id),
  };

  const res = await fetchAgente(`${base}/imprimir`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await fiscalError(res, 'El controlador fiscal respondió con error');
  // Devuelve el número de comprobante emitido (para guardarlo y poder reimprimir)
  const data = await res.json().catch(() => null);
  return data?.numero_comprobante ?? null;
}

/**
 * Reimprime (copia) un comprobante fiscal ya emitido, por su número.
 * NO emite uno nuevo: usa CopiarComprobante del controlador.
 */
export async function reprintFiscal(numero: string, codigo?: string): Promise<void> {
  return conRegistro('reimprimir', null, () => _reprintFiscal(numero, codigo), () => numero);
}

async function _reprintFiscal(numero: string, codigo?: string): Promise<void> {
  const base = getFiscalAgentUrl().replace(/\/$/, '');
  const res = await fetchAgente(`${base}/reimprimir`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ numero, codigo }),
  });
  if (!res.ok) throw await fiscalError(res, 'No se pudo reimprimir');
}

// Fecha ISO -> AAMMDD (formato que pide el controlador para el comprobante asociado)
function toAAMMDD(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}${mm}${dd}`;
}

/**
 * Emite una Nota de Crédito fiscal que revierte/anula un comprobante ya emitido.
 * Requiere que la venta tenga guardado su número de comprobante (fiscal_comprobante).
 */
export async function notaCreditoFiscal(sale: Sale, opts?: FiscalOptions): Promise<string | null> {
  return conRegistro('nota-credito', sale.id, () => _notaCreditoFiscal(sale, opts));
}

async function _notaCreditoFiscal(sale: Sale, opts?: FiscalOptions): Promise<string | null> {
  const base = getFiscalAgentUrl().replace(/\/$/, '');
  const numero = sale.fiscal_comprobante || '';
  if (!numero) throw new Error('La venta no tiene comprobante fiscal para anular');

  const payload = {
    tipo_comprobante: 'C',
    condicion_iva_emisor: 'monotributo',
    emisor_cuit: opts?.cuit || '',
    cliente: {
      nombre: sale.customer_name || 'Consumidor Final',
      documento: sale.customer_document || '',
    },
    items: (sale.items || []).map((it) => ({
      descripcion: it.product_name || it.custom_name || 'Producto',
      cantidad: Number(it.quantity) || 1,
      precio_unitario: Number(it.unit_price) || 0,
      importe: Number(it.subtotal) || 0,
      alicuota_iva: 0,
    })),
    descuento: Math.max(0, (Number(sale.subtotal) || 0) - (Number(sale.total) || 0)),
    total: Number(sale.total) || 0,
    // Comprobante original que esta NC anula/revierte
    original: {
      numero,
      fecha: toAAMMDD(sale.created_at),
      codigo: 'Tique',
    },
  };

  const res = await fetchAgente(`${base}/nota-credito`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await fiscalError(res, 'No se pudo emitir la Nota de Crédito');
  const data = await res.json().catch(() => null);
  return data?.numero_comprobante ?? null;
}

/**
 * Reporte X: lectura parcial de la jornada en curso. Imprime el acumulado del día
 * SIN cerrar nada y sin tocar los contadores fiscales, asi que se puede repetir.
 * Sirve para contestar "¿la impresora esta imprimiendo?" sin arriesgar la jornada,
 * que con el Cierre Z no se puede porque no tiene vuelta atras.
 */
export async function reporteXFiscal(): Promise<string | null> {
  return conRegistro('reporte-x', null, () => _reporteXFiscal());
}

async function _reporteXFiscal(): Promise<string | null> {
  const base = getFiscalAgentUrl().replace(/\/$/, '');
  const res = await fetchAgente(`${base}/reporte-x`, { method: 'POST' });
  if (!res.ok) throw await fiscalError(res, 'No se pudo emitir el Reporte X');
  const data = await res.json().catch(() => null);
  return data?.numero ?? null;
}

/**
 * Cierre Z (Informe Diario de Cierre): cierra la jornada fiscal del día.
 * Se hace al finalizar cada jornada (AFIP lo exige). Imprime el reporte Z.
 */
export async function cierreZFiscal(): Promise<string | null> {
  return conRegistro('cierre-z', null, () => _cierreZFiscal());
}

async function _cierreZFiscal(): Promise<string | null> {
  const base = getFiscalAgentUrl().replace(/\/$/, '');
  const res = await fetchAgente(`${base}/cierre-z`, { method: 'POST' });
  if (!res.ok) throw await fiscalError(res, 'No se pudo hacer el Cierre Z');
  const data = await res.json().catch(() => null);
  return data?.numero ?? null;
}
